import { zodResolver } from '@hookform/resolvers/zod'
import {
  type BaseSyntheticEvent,
  type FocusEventHandler,
  type JSX,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Form, type FormProps } from '#src/components/core/form/Form.tsx'
import {
  type Field,
  type FieldState,
  FormField,
  type FormState,
} from '#src/components/core/form/FormField.tsx'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'

// react-hook-form requires form values to not be undefined. This is because React has issues when a form field goes from uncontrolled (undefined value) to controlled (defined value).
export type ValidFormValue<TValue> = NonNullable<TValue> | null

export type FormValueSchema<TValue> =
  | z.ZodType<ValidFormValue<TValue>>
  | z.ZodEffects<z.ZodType<ValidFormValue<TValue>>>
  | z.ZodEffects<z.ZodEffects<z.ZodType<ValidFormValue<TValue>>>> // Note: If we need more levels of effects we can add additional nested effects here. If there's a more elegant way to do this please improve it!

export interface FieldFormProps<
  TValue,
  TFormValue = ValidFormValue<TValue>,
> extends Omit<FormProps, 'defaultValue' | 'onSubmit'> {
  defaultValue: TFormValue
  onSubmit: (value: TFormValue, event?: BaseSyntheticEvent) => void
  render: FieldFormRender<TFormValue>
  disabled?: boolean
  valueSchema?: FormValueSchema<TFormValue>
  valueSchemaOptions?: Partial<z.ParseParams>
}

export type FieldFormRender<TValue> = ({
  field,
  fieldState,
  formState,
}: {
  field: Field<TValue>
  fieldState: FieldState
  formState: Omit<
    FormState,
    | 'defaultValues'
    | 'dirtyFields'
    | 'touchedFields'
    | 'validatingFields'
    | 'errors'
  >
}) => ReactElement<any>

// Note that FieldForm debounces submitting the form while the field is being changed. The debounced function is flushed when the form is blurred.
export function FieldForm<TValue>({
  defaultValue,
  onSubmit,
  render,
  disabled,
  valueSchema,
  valueSchemaOptions,
  onFocus,
  onBlur,
  ...delegated
}: FieldFormProps<TValue>): JSX.Element {
  const form = useForm({
    defaultValues: {
      value: defaultValue,
    },
    resolver: valueSchema
      ? zodResolver(z.object({ value: valueSchema }), valueSchemaOptions)
      : undefined,
  })

  const handleSubmit = form.handleSubmit((data) => onSubmit(data.value))

  const renderWithOnChange = useCallback<
    FieldFormRender<ValidFormValue<TValue>>
  >(
    ({
      field: { onChange: fieldOnChange, ...fieldRest },
      fieldState,
      formState,
    }) =>
      render({
        field: {
          onChange: (value) => {
            fieldOnChange(value)
            void handleSubmit()
          },
          ...fieldRest,
        },
        fieldState,
        formState,
      }),
    [render, handleSubmit]
  )

  const [isFocused, setIsFocused] = useState(false)
  const handleFocus: FocusEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      setIsFocused(true)
      onFocus?.(event)
    },
    [onFocus]
  )
  const handleBlur: FocusEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      setIsFocused(false)
      onBlur?.(event)
    },
    [onBlur]
  )

  const reset = form.reset
  const focusRef = useCurrentRef(isFocused) // We use a ref here because we don't want to reset the form below every time focus changes.
  useEffect(() => {
    // We put this in useEffect instead of in the render function because react-hook-form recommends using reset inside useEffect: https://react-hook-form.com/docs/useform/reset
    if (!focusRef.current) {
      reset({ value: defaultValue })
    }
  }, [defaultValue, reset, focusRef])

  return (
    <Form
      onSubmit={handleSubmit}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...delegated}
    >
      <FormField
        control={form.control}
        name="value"
        render={renderWithOnChange}
        disabled={disabled}
      />
    </Form>
  )
}
